import { GenericTable } from "@/components/GenericTable/GenericTable";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useMedicationRequestByOrganization } from "@/services/fhir/medicationrequest/hooks/useMedicationRequestByOrganization";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Tab,
  Tabs,
  useTheme,
  Divider,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SearchIcon from "@mui/icons-material/Search";
import { useDict } from "@/utils/useDict";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { NoData } from "@/components/common/NoData";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prescription-tabpanel-${index}`}
      aria-labelledby={`prescription-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface MedicationStatusStats {
  status: string;
  count: number;
  percentage: number;
}

interface MedicationDetail {
  code: string;
  label: string;
  count: number;
  percentage: number;
}

interface PatientMedicationData {
  medication: string;
  code: string;
  status: string;
  date: string;
}

interface PatientMedicationGroup {
  patientId: string;
  patientName: string;
  totalMedications: number;
  medications: PatientMedicationData[];
  statusCounts: {
    active: number;
    "on-hold": number;
    cancelled: number;
    completed: number;
    "entered-in-error": number;
    draft: number;
    stopped: number;
    unknown: number;
  };
}

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;

  const maxLabelLength = Math.max(...data.map((item) => item.label.length));

  // Cada caractere ocupa aproximadamente 7px
  // Adiciona padding de 20px
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);

  return Math.min(calculatedWidth, 400); // Máximo de 400px
};

export function PrescriptionAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientMedications, setPatientMedications] = useState<
    PatientMedicationData[]
  >([]);
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [medicationSearch, setMedicationSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({});

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  const {
    data: medData,
    loading: medLoading,
    fetchMedicationRequests,
  } = useMedicationRequestByOrganization({
    organizationId,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    count: count || 250,
    includeEncounter: false,
  });

  // Processar os dados por status
  const { medicationStatusStats, totalRecords } = useMemo(() => {
    const statusMap = new Map<string, number>();
    const medications = medData?.medicationRequests || [];

    for (const med of medications) {
      const status = med.status || "unknown";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    }

    const total = medications.length;
    const stats: MedicationStatusStats[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      medicationStatusStats: stats,
      totalRecords: total,
    };
  }, [medData?.medicationRequests]);

  // Processar medicamentos detalhados para a tabela
  const { medicationDetails } = useMemo(() => {
    const counts = new Map<string, number>();
    const labels = new Map<string, string>();
    const medications = medData?.medicationRequests || [];

    // Lista de códigos a serem ignorados
    const ignoredCodes = ["Não informado", "", undefined, null];

    for (const med of medications) {
      const codingWithCode = med.medication?.concept?.coding?.find(
        (c) => !!c.code
      );
      const codeKey = codingWithCode?.code || "Não informado";
      const displayLabel =
        codingWithCode?.display || med.medication?.concept?.text || codeKey;

      // Pular medicamentos com código não informado ou medicação não assinada
      if (
        ignoredCodes.includes(codeKey as any) ||
        !med.medication?.concept?.text ||
        displayLabel === "Não informado"
      ) {
        continue;
      }

      counts.set(codeKey, (counts.get(codeKey) || 0) + 1);
      if (!labels.has(codeKey)) labels.set(codeKey, displayLabel);
    }

    const total = medications.length;
    const details: MedicationDetail[] = Array.from(counts.entries())
      .map(([code, count]) => ({
        code,
        label: labels.get(code) || code,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      medicationDetails: details,
    };
  }, [medData?.medicationRequests]);

  // Mapa de tradução de status
  const statusTranslation: Record<string, string> = {
    active: "Ativo",
    "on-hold": "Suspenso",
    cancelled: "Cancelado",
    completed: "Concluído",
    "entered-in-error": "Erro",
    stopped: "Interrompido",
    draft: "Solicitado",
    unknown: "Desconhecido",
  };

  // Dados para o gráfico de status
  const medicationStatusChartData = useMemo(() => {
    return medicationStatusStats.map((stat) => ({
      label: statusTranslation[stat.status] || stat.status || "Não informado",
      value: stat.count,
    }));
  }, [medicationStatusStats]);

  // Processar todos os medicamentos agrupados
  const allMedicationData = useMemo(() => {
    return medicationDetails
      .map((d) => ({
        label: d.label,
        value: d.count,
        code: d.code,
      }))
      .sort((a, b) => b.value - a.value);
  }, [medicationDetails]);

  // Processar top 5 medicamentos para o gráfico
  const topMedicationData = useMemo(() => {
    return allMedicationData.slice(0, 5).sort((a, b) => a.value - b.value);
  }, [allMedicationData]);

  // Filtrar medicamentos para a tabela de pesquisa
  const filteredMedicationData = useMemo(() => {
    if (!medicationSearch.trim()) return allMedicationData;
    const q = medicationSearch.toLowerCase();
    return allMedicationData.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.code?.toLowerCase().includes(q)
    );
  }, [allMedicationData, medicationSearch]);

  // Filtrar medicamentos detalhados baseado na busca
  const filteredMedications = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return medicationDetails;
    return medicationDetails.filter(
      (m) =>
        m.label?.toLowerCase().includes(q) || m.code?.toLowerCase().includes(q)
    );
  }, [medicationDetails, search]);

  // Agrupar medicamentos por paciente
  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientMedicationGroup>();
    const medications = medData?.medicationRequests || [];
    const patients = medData?.patients || [];
    const patientMap = new Map<string, fhir5.Patient>();

    // Criar mapa de pacientes para acesso rápido
    patients.forEach((patient) => {
      if (patient.id) {
        patientMap.set(patient.id, patient);
      }
    });

    for (const med of medications) {
      const patientRef = med.subject?.reference;
      if (!patientRef) continue;

      const patientId = patientRef.split("/").pop() || "";
      if (!patientId) continue;

      const patient = patientMap.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalMedications: 0,
          medications: [],
          statusCounts: {
            active: 0,
            "on-hold": 0,
            cancelled: 0,
            completed: 0,
            "entered-in-error": 0,
            draft: 0,
            stopped: 0,
            unknown: 0,
          },
        });
      }

      const group = groups.get(patientId)!;
      const codingWithCode = med.medication?.concept?.coding?.find(
        (c) => !!c.code
      );
      const medicationCode = codingWithCode?.code || "Não informado";
      const medicationLabel =
        codingWithCode?.display ||
        med.medication?.concept?.text ||
        medicationCode;
      const status = med.status || "unknown";
      const medicationDate = med.authoredOn
        ? new Date(med.authoredOn).toLocaleDateString()
        : med.effectiveDosePeriod?.start
        ? new Date(med.effectiveDosePeriod.start).toLocaleDateString()
        : med.meta?.lastUpdated
        ? new Date(med.meta.lastUpdated).toLocaleDateString()
        : "Não informado";

      group.totalMedications++;
      group.medications.push({
        medication: medicationLabel,
        code: medicationCode,
        status,
        date: medicationDate,
      });

      // Contar status
      if (status in group.statusCounts) {
        group.statusCounts[status as keyof typeof group.statusCounts]++;
      } else {
        group.statusCounts.unknown++;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.totalMedications - a.totalMedications
    );
  }, [medData?.medicationRequests, medData?.patients]);

  // Filtrar grupos de pacientes baseado na busca
  const filteredPatientGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? patientGroups.filter(
          (group) =>
            group.patientName?.toLowerCase().includes(q) ||
            group.medications.some(
              (m) =>
                m.medication?.toLowerCase().includes(q) ||
                m.code?.toLowerCase().includes(q)
            )
        )
      : patientGroups;

    return filtered.map((group) => ({
      patientName: group.patientName,
      totalMedications: group.totalMedications,
      active: group.statusCounts.active,
      onHold: group.statusCounts["on-hold"],
      cancelled: group.statusCounts.cancelled,
      completed: group.statusCounts.completed,
      draft: group.statusCounts.draft,
      stopped: group.statusCounts.stopped,
    }));
  }, [patientGroups, search]);

  // Filtrar medicamentos do dialog baseado na busca
  const filteredDialogMedications = useMemo(() => {
    const q = dialogSearch.trim().toLowerCase();
    return q
      ? patientMedications.filter(
          (medication) =>
            medication.medication?.toLowerCase().includes(q) ||
            medication.code?.toLowerCase().includes(q) ||
            medication.status?.toLowerCase().includes(q)
        )
      : patientMedications;
  }, [patientMedications, dialogSearch]);

  // Função para lidar com o clique na linha da tabela de pacientes
  const handlePatientRowClick = (row: any) => {
    const group = patientGroups.find((g) => g.patientName === row.patientName);
    if (group) {
      setSelectedPatient(group);
      setPatientMedications(group.medications);
      setDialogSearch("");
      setPatientDialogOpen(true);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchMedicationRequests();
    }
  }, [
    organizationId,
    fetchMedicationRequests,
    dateRange,
    selectedLocation,
    selectedPractitioner,
  ]);

  // Renderizar skeleton durante carregamento
  if (medLoading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Tratamentos prescritos"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {totalRecords > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Status das Prescrições" />
              <Tab label="Detalhamento" />
            </Tabs>
          </Box>

          {/* Aba de Status das Prescrições */}
          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                <Stack spacing={5}>
                  {/* Gráfico de Status de Prescrições */}
                  {medicationStatusChartData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const sorted = [...medicationStatusChartData].sort(
                            (a, b) => b.value - a.value
                          );
                          const total = medicationStatusChartData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((sorted[0].value / total) * 100)
                              : 0;
                          return `${percentage}% das prescrições estão como '${sorted[0].label}'`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Monitore o andamento dos tratamentos. Um número alto de
                        prescrições "canceladas" ou "suspensas" pode indicar
                        problemas na adesão do paciente.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        {medicationStatusChartData &&
                        medicationStatusChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={medicationStatusChartData}
                              layout="vertical"
                              margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis
                                dataKey="label"
                                type="category"
                                width={calculateYAxisWidth(
                                  medicationStatusChartData
                                )}
                                tick={{ fontSize: 12 }}
                              />
                              <RechartsTooltip
                                formatter={(value: number | undefined) => {
                                  if (value === undefined)
                                    return ["0", "Quantidade"];
                                  const total =
                                    medicationStatusChartData.reduce(
                                      (acc, item) => acc + item.value,
                                      0
                                    );
                                  const percentage =
                                    total > 0
                                      ? Math.round((value / total) * 100)
                                      : 0;
                                  return [
                                    `${value} (${percentage}%)`,
                                    "Quantidade",
                                  ];
                                }}
                              />
                              <Bar dataKey="value" name="Quantidade">
                                {medicationStatusChartData.map(
                                  (entry, index) => {
                                    const maxValue = Math.max(
                                      ...medicationStatusChartData.map(
                                        (d) => d.value
                                      )
                                    );
                                    const isHighlight =
                                      entry.value === maxValue;
                                    return (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={
                                          isHighlight
                                            ? theme.palette.primary.main
                                            : "#9E9E9E"
                                        }
                                      />
                                    );
                                  }
                                )}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                            sx={{ py: 4 }}
                          >
                            Nenhum dado disponível
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de status de prescrição." />
                  )}

                  {/* Gráfico de Top 5 Medicamentos */}
                  {topMedicationData.length > 0 && <Divider />}

                  {topMedicationData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const sorted = [...topMedicationData].sort(
                            (a, b) => b.value - a.value
                          );
                          const total = allMedicationData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((sorted[0].value / total) * 100)
                              : 0;
                          return `'${sorted[0].label}' representa ${percentage}% das prescrições`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Veja os 5 medicamentos mais prescritos. Útil para
                        gerenciar estoque e entender os tratamentos mais
                        frequentes. Use a tabela abaixo para pesquisar todos.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={topMedicationData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(topMedicationData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = allMedicationData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {topMedicationData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...topMedicationData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* Tabela de todos os medicamentos */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Todos os Medicamentos ({allMedicationData.length}{" "}
                          tipos diferentes)
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="Buscar medicamento..."
                            value={medicationSearch}
                            onChange={(e) =>
                              setMedicationSearch(e.target.value)
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <GenericTable
                          tableColumns={[
                            {
                              column: "Medicamento",
                              rowName: "label",
                              align: "left",
                            },
                            {
                              column: "Quantidade",
                              rowName: "value",
                              width: "120px",
                            },
                            {
                              column: "Percentual",
                              rowName: "percentage",
                              width: "120px",
                            },
                          ]}
                          tableRows={filteredMedicationData.map((item) => {
                            const total = allMedicationData.reduce(
                              (acc, i) => acc + i.value,
                              0
                            );
                            const percentage =
                              total > 0
                                ? Math.round((item.value / total) * 100)
                                : 0;
                            return {
                              ...item,
                              percentage: `${percentage}%`,
                            };
                          })}
                          characterLimit={80}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de medicamentos prescritos." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Aba de Detalhamento */}
          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por paciente ou medicamento"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    {
                      column: "Paciente",
                      rowName: "patientName",
                      align: "left",
                    },
                    { column: "Ativo", rowName: "active", width: "80px" },
                    { column: "Em Espera", rowName: "onHold", width: "100px" },
                    {
                      column: "Cancelado",
                      rowName: "cancelled",
                      width: "100px",
                    },
                    {
                      column: "Concluído",
                      rowName: "completed",
                      width: "100px",
                    },
                    {
                      column: "Total",
                      rowName: "totalMedications",
                      width: "80px",
                    },
                  ]}
                  tableRows={filteredPatientGroups}
                  characterLimit={60}
                  onRowClick={handlePatientRowClick}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma prescrição encontrada
          </Typography>
        </Box>
      )}

      {/* Dialog para exibir medicamentos do paciente */}
      {patientDialogOpen && selectedPatient && (
        <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Medicamentos de: {selectedPatient.patientName}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Buscar por medicamento ou status"
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredDialogMedications.length > 0 ? (
              <GenericTable
                tableColumns={[
                  {
                    column: "Medicamento",
                    rowName: "medication",
                    align: "left",
                  },
                  { column: "Status", rowName: "statusLabel", width: "150px" },
                  { column: "Data", rowName: "date", width: "120px" },
                ]}
                tableRows={filteredDialogMedications.map((medication) => ({
                  medication: medication.medication,
                  statusLabel:
                    useDict.medicationRequestStatus[medication.status] ||
                    medication.status ||
                    "Não informado",
                  date: medication.date,
                }))}
                characterLimit={80}
              />
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 2 }}>
                {dialogSearch
                  ? "Nenhum medicamento encontrado com os critérios de busca."
                  : "Nenhum medicamento encontrado para este paciente."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPatientDialogOpen(false);
                setSelectedPatient(null);
                setPatientMedications([]);
                setDialogSearch("");
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
